import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import { executeScroll, isEmpty } from "../../utils/utils";
import NumberInput from "../../framework/NumberInput";
import { searchBranchGroup } from "../../services/branch-group-services";
import SelectInput from "../../framework/SelectInput";
const BranchGroupFilter = ({
  reportRef,
  onFilterShow,
  applyFilterOnMount = true,
  refreshData = false,
}) => {
  const [branchGrp, setBranchGrp] = useState("");
  const [limit, setLimit] = useState(100);
  const [orderBy] = useState([{ fieldName: "updatedTime", orderType: "desc" }]);
  const [branchGrpSuggestions, setBranchGrpSuggestions] = useState([]);
  const branchGrpRef = useRef();
  const applyFilterRef = useRef();
  const filterLimitRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};

      if (!isEmpty(branchGrp)) {
        filters.push(createFilterParam("name", branchGrp, "string", LIKE));
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }
      if (orderBy) {
        result.orderBy = orderBy;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;
      reportRef.current?.applyFilters(result);
    },
    [branchGrp, limit, orderBy, reportRef]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="amr_filter_branch_grp_name"
            label="Branch Group"
            ref={branchGrpRef}
            options={branchGrpSuggestions}
            getOptionLabel={(option) => option?.name || option}
            disableClearable={false}
            noOptionsText="Search Branch Group"
            onFocus={() => setCurrentComponent("amr_filter_branch_grp_name")}
            onChange={(e, values) => {
              setBranchGrp(values?.name);
            }}
            value={branchGrp}
            onInputChange={async (e, value) => {
              const newValue = e.target.value;
              setBranchGrp(e.target.value);
              if (newValue?.length >= 2) {
                const payload = {
                  filters: [
                    {
                      fieldName: "name",
                      fieldType: { type: "string", operation: "like" },
                      fieldValue: newValue,
                    },
                  ],
                  orderBy: [{ fieldName: "name", orderType: "asc" }],
                };
                const resp = await searchBranchGroup(payload);
                setBranchGrpSuggestions(resp);
              }
            }}
            markers={JSON.stringify({
              up: "amr_filter_branch_grp_name",
              down: "amr_limit_filter",
              right: "amr_limit_filter",
              left: "amr_filter_branch_grp_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="amr_limit_filter"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("amr_limit_filter");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "amr_filter_branch_grp_name",
                down: "amr_apply_filter_button",
                left: "amr_filter_branch_grp_name",
                right: "amr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="amr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "amr_limit_filter",
              left: "amr_limit_filter",
              right: "amr_apply_filter_button",
              down: "amr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      branchGrpRef?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default BranchGroupFilter;
